import { useTranslation } from "react-i18next"
import { LogoutIcon, TrashCanIcon } from "../../../../assets/customermenu-icons"
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout"
import styles from "./profile-details-page.module.scss"
import { useDispatch, useSelector } from "react-redux"
import { RootState } from "../../../../stores/store"
import { useState } from "react"
import { EditAccountNameModal, EditCorporateNameModal, EditPhoneNumberModal } from "./components"
import { useNavigate } from "react-router-dom"
import { useLogoutMutation } from "../../../../service/authentication-service"
import { useDeleteUserMutation, useCompleteUpdatePhoneNumberMutation, useGetUserInfoQuery, useUpdatePhoneNumberMutation } from "../../../../service/user-service"
import { CustomerType } from "../../../../model/customer/customer-type"
import { logOut, updateCustomerInfo } from "../../../../stores/slices/current-user-slice"
import { ApplicationRoutes } from "../../../../routes"
import { toast } from "react-toastify"
import { ConfirmationModal } from "../../components"
import OTPVerification from "../../components/otp-verification"
import { useCan } from "../../../../guards/useCan"
import { CustomerPermissions } from "../../../../model/permissions/permissions"

export const ProfileDetailsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigator = useNavigate();
  const userInfo = useSelector((state: RootState) => state.user)

  const { can } = useCan();
  const canEditProfile = can(CustomerPermissions.EditAccountProfileDetails);

  const [logoutApi] = useLogoutMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [completeUpdatePhoneNumber, { isLoading: isVerifying }] = useCompleteUpdatePhoneNumberMutation();
  const [updatePhoneNumber] = useUpdatePhoneNumberMutation();

  const { data: fullUserInfo } = useGetUserInfoQuery();
  const isCorporate = userInfo.customerType === CustomerType.Corporate;
  const organizationName = fullUserInfo?.customerDetails?.organizationName ?? "";

  // Modal states
  const [isEditAccountNameModalOpen, setIsEditAccountNameModalOpen] = useState(false)
  const [isEditCorporateNameModalOpen, setIsEditCorporateNameModalOpen] = useState(false)
  const [isEditPhoneNumberModalOpen, setIsEditPhoneNumberModalOpen] = useState(false)
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false)

  // OTP verification states
  const [isOTPVerification, setIsOTPVerification] = useState(false)
  const [otp, setOtp] = useState<string>("");
  const [newPhoneNumber, setNewPhoneNumber] = useState<string>("");

  const handleEditAccountName = () => {
    setIsEditAccountNameModalOpen(true)
  }

  const handleCloseAccountNameModal = () => {
    setIsEditAccountNameModalOpen(false)
  }

  const handleEditPhoneNumber = () => {
    setIsEditPhoneNumberModalOpen(true)
  }

  const handleClosePhoneNumberModal = () => {
    setIsEditPhoneNumberModalOpen(false)
  }

  const handleDeleteAccountClick = () => {
    setIsDeleteAccountModalOpen(true)
  }

  const handleCloseDeleteAccountModal = () => {
    setIsDeleteAccountModalOpen(false)
  }

  const handleConfirmDeleteAccount = async () => {
    try {
      await deleteUser().unwrap();
      handleCloseDeleteAccountModal();
      dispatch(logOut());
      navigator(ApplicationRoutes.LANDING_PAGE);
    } catch (error: any) {
      toast.error(error?.data?.title || t("account.web.failedToDeleteAccount"));
    }
  }

  const logout = async () => {
    await logoutApi()
    dispatch(logOut());
    navigator(ApplicationRoutes.LANDING_PAGE);
  }

  const handleOtpSent = (phoneNumber: string) => {
    setNewPhoneNumber(phoneNumber)
    setIsOTPVerification(true)
  }

  const handleResendOTP = async () => {
    try {
      await updatePhoneNumber({
        phoneNumber: newPhoneNumber
      }).unwrap();
      setOtp("");
      toast.success(t("account.web.otpResentSuccessfully"));
    } catch (error: any) {
      toast.error(error?.data?.title || t("account.web.failedToResendOtp"));
    }
  };

  const handleOTPSubmit = async () => {
    if (otp.length !== 4) {
      toast.error(t("account.web.enterValid4DigitOtp"));
      return;
    }

    try {
      const response = await completeUpdatePhoneNumber(otp).unwrap();

      dispatch(updateCustomerInfo({
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        countryCode: userInfo.countryCode,
        phoneNumber: response.phoneNumber,
        hasPin: userInfo.hasPin || false,
        image: userInfo.image || "",
        email: userInfo.email || "",
        customerType: userInfo.customerType
      }));

      toast.success(t("account.web.phoneNumberUpdatedSuccessfully"));
      setIsOTPVerification(false);
      setOtp("");
      setNewPhoneNumber("");
    } catch (error: any) {
      toast.error(error?.data?.title || t("account.web.invalidOtpCode"));
      setOtp("");
    }
  };

  const handleCancelOTPVerification = () => {
    setIsOTPVerification(false);
    setOtp("");
    setNewPhoneNumber("");
  };

  if (isOTPVerification) return (
    <CustomerPageLayout title={t("common.labels.phoneNumberVerification")} onBack={handleCancelOTPVerification}>
      <div className={styles.otpVerificationContainer}>
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>{t("common.labels.phoneNumberVerification")}</h2>
          <p className={styles.subtitle}>{t("common.labels.anOtpHasBeenSent", { provider: newPhoneNumber })}</p>
        </div>

        <OTPVerification
          value={otp}
          onChange={setOtp}
          onSubmit={handleOTPSubmit}
          onResend={handleResendOTP}
          buttonText={isVerifying ? t("account.web.verifying") : t("common.actions.continueKey")}
          otpLength={4}
          resendTimerSeconds={180}
          disabled={isVerifying}
          error={false}
        />
      </div >
    </CustomerPageLayout>
  )

  const fieldRow = (label: string, value: string, onClick?: () => void, isPlaceholder?: boolean) => (
    <button type="button" className={styles.fieldRow} onClick={onClick} disabled={!onClick}>
      <span className={styles.fieldRowText}>
        <span className={styles.fieldRowLabel}>{label}</span>
        <span className={`${styles.fieldRowValue} ${isPlaceholder ? styles.fieldRowPlaceholder : ""}`}>{value}</span>
      </span>
      {onClick && <span className={styles.fieldRowChevron}>›</span>}
    </button>
  )

  return (
    <CustomerPageLayout title={t("account.accountInformation")} >
      <div className={styles.profileDetailsContainer}>

        {/* Profile Photo */}
        <div className={styles.photoSection}>
          <button
            type="button"
            className={styles.photoCircle}
            onClick={canEditProfile ? () => navigator(`/${ApplicationRoutes.SETTINGS}/${ApplicationRoutes.PROFILE_PICTURE}`) : undefined}
            disabled={!canEditProfile}
            aria-label={t("account.web.changeProfilePhoto")}
          >
            {userInfo?.image
              ? <img src={userInfo.image} alt={t("account.web.profileImageAlt")} />
              : <span className={styles.photoInitial}>{(userInfo.firstName || "").charAt(0).toUpperCase()}</span>}
          </button>
          {canEditProfile && <span className={styles.photoHint}>{t("common.labels.tapToChange")}</span>}
        </div>

        <div className={styles.fieldsCard}>
          {isCorporate && fieldRow(t("account.corporateName"), organizationName, canEditProfile ? () => setIsEditCorporateNameModalOpen(true) : undefined)}
          {fieldRow(t("account.email"), userInfo.userName || "")}
          {fieldRow(
            isCorporate ? t("account.contactName") : t("solar.fullName"),
            `${userInfo.firstName || ""} ${userInfo.lastName || ""}`.trim(),
            canEditProfile ? handleEditAccountName : undefined
          )}
          {fieldRow(
            isCorporate ? t("account.contactNumber") : t("common.labels.phoneNumber"),
            userInfo.phoneNumber || "",
            canEditProfile ? handleEditPhoneNumber : undefined
          )}
        </div>


        <div className={styles.actionsSection}>
          {/* Logout */}
          <button className={styles.actionButton} onClick={logout}>
            <div className={styles.actionLeft}>
              <div className={styles.actionIcon}>
                <LogoutIcon width={18} height={18} />
              </div>
              <span className={styles.actionLabel}>{t("account.logout")}</span>
            </div>
          </button>

          {/* Delete Account — destructive, visually separated */}
          <button className={`${styles.actionButton} ${styles.deleteButton}`} onClick={handleDeleteAccountClick}>
            <div className={styles.actionLeft}>
              <div className={`${styles.actionIcon} ${styles.actionIconDanger}`}>
                <TrashCanIcon width={20} height={20} />
              </div>
              <span className={`${styles.actionLabel} ${styles.actionLabelDanger}`}>{t("common.labels.deleteAccount")}</span>
            </div>
          </button>
        </div>

        {/* Edit Account Name Modal */}
        <EditAccountNameModal
          isOpen={isEditAccountNameModalOpen}
          onClose={handleCloseAccountNameModal}
          firstName={userInfo.firstName}
          lastName={userInfo.lastName}
        />

        {/* Edit Corporate Name Modal */}
        {isCorporate && (
          <EditCorporateNameModal
            isOpen={isEditCorporateNameModalOpen}
            onClose={() => setIsEditCorporateNameModalOpen(false)}
            organizationName={organizationName}
          />
        )}

        {/* Edit Phone Number Modal */}
        <EditPhoneNumberModal
          isOpen={isEditPhoneNumberModalOpen}
          onClose={handleClosePhoneNumberModal}
          onOtpSent={handleOtpSent}
          phoneNumber={userInfo.phoneNumber || ""}
        />

        {/* Delete Account Confirmation Modal */}
        <ConfirmationModal
          isOpen={isDeleteAccountModalOpen}
          onClose={handleCloseDeleteAccountModal}
          onConfirm={handleConfirmDeleteAccount}
          title={t("common.labels.deleteAccount")}
          message={t("account.web.deleteAccountConfirmMessage")}
          confirmText={t("common.labels.deleteAccount")}
          loadingText={t("account.web.deleting")}
          isLoading={isDeleting}
          variant="danger"
        />
      </div>
    </CustomerPageLayout>
  )
}